import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useUser } from "../../apis/user/UserProvider";
import { getPageName, getUiInterface } from "../../locales/translationUtils";

interface ChangePasswordModalProps {
  show: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ show, onClose }) => {
  const { userLogin, updatePassword } = useUser();

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);

  const currentPasswordRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (show) {
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setNewPasswordError(null);
      setConfirmPasswordError(null);
    }
  }, [show]);

  useEffect(() => {
    if (show && currentPasswordRef.current) {
      currentPasswordRef.current.focus();
    }
  }, [show]);
  

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    if (name === "newPassword") {
      setNewPasswordError(value.length < 6 ? getUiInterface("passwordMinimumLength") : null);
    }
    if (name === "confirmPassword") {
      setConfirmPasswordError(value !== formData.newPassword ? getUiInterface("passwordEquals") : null);
    }
  };

  const handleChangePassword = async () => {
    if (!userLogin?.username) {
      alert("Erro: Usuário não identificado.");
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      alert("Erro: As senhas não coincidem.");
      return;
    }

    try {
      await updatePassword({
        usuarioId: userLogin.id,
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      alert("Senha alterada com sucesso!");
      onClose();
    } catch (error) {
      alert("Erro ao alterar senha. Tente novamente.");
    }
  };

  const handleButtonDisabled = () => {
    return (
      !formData.currentPassword ||
      formData.newPassword.length < 6 ||
      formData.newPassword !== formData.confirmPassword
    );
  };

  return (
    <Modal show={show} onHide={onClose} backdrop="static" keyboard={false} centered>
      <Modal.Header closeButton>
        <Modal.Title>{getPageName("changePassword")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group controlId="formCurrentPassword" className="mb-3">
            <Form.Label>{getUiInterface("currentPassword")}:</Form.Label>
            <Form.Control
              type="password"
              name="currentPassword"
              value={formData.currentPassword}
              ref={currentPasswordRef}
              onChange={handleInputChange}
            />
          </Form.Group>
          <Form.Group controlId="formNewPassword" className="mb-3">
            <Form.Label>{getUiInterface("newPassword")}:</Form.Label>
            <Form.Control
              type="password"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleInputChange}
            />
            {newPasswordError && (
              <Form.Text className="text-danger">{newPasswordError}</Form.Text>
            )}
          </Form.Group>
          <Form.Group controlId="formConfirmPassword" className="mb-3">
            <Form.Label>{getUiInterface("confirmPassword")}:</Form.Label>
            <Form.Control
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
            />
            {confirmPasswordError && (
              <Form.Text className="text-danger">{confirmPasswordError}</Form.Text>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {getUiInterface("cancel")}
        </Button>
        <Button variant="primary" disabled={handleButtonDisabled()} onClick={handleChangePassword}>
          {getUiInterface("changePassword")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangePasswordModal;